import React, { useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { nodeLabels, nodeColors, nodeIcons } from './FlowNodes';
import { authAxios, API } from '@/contexts/AuthContext';

export default function PropertiesPanel({ node, onUpdate, onClose, media }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = React.useState(false);

  if (!node) return null;

  const nt = node.data.node_type;
  const config = node.data.config || {};
  const colors = nodeColors[nt] || nodeColors.text_message;
  const Icon = nodeIcons[nt];
  const label = nodeLabels[nt];

  const updateConfig = (key, value) => {
    onUpdate(node.id, { ...node.data, config: { ...config, [key]: value } });
  };

  const updateTitle = (value) => {
    onUpdate(node.id, { ...node.data, title: value });
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await authAxios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      updateConfig('media_url', data.url);
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const addOption = () => {
    const opts = [...(config.options || [])];
    opts.push({ id: `opt_${Date.now()}`, label: '', value: '' });
    updateConfig('options', opts);
  };

  const updateOption = (idx, field, value) => {
    const opts = [...(config.options || [])];
    opts[idx] = { ...opts[idx], [field]: value };
    updateConfig('options', opts);
  };

  const removeOption = (idx) => {
    updateConfig('options', (config.options || []).filter((_, i) => i !== idx));
  };

  const moveOption = (idx, dir) => {
    const opts = [...(config.options || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= opts.length) return;
    [opts[idx], opts[newIdx]] = [opts[newIdx], opts[idx]];
    updateConfig('options', opts);
  };

  const addBranch = () => {
    const branches = [...(config.branches || [])];
    branches.push({ id: `br_${Date.now()}`, label: '', type: 'equals', value: '' });
    updateConfig('branches', branches);
  };

  const updateBranch = (idx, field, value) => {
    const branches = [...(config.branches || [])];
    branches[idx] = { ...branches[idx], [field]: value };
    updateConfig('branches', branches);
  };

  const removeBranch = (idx) => {
    updateConfig('branches', (config.branches || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="w-80 bg-[#0f172a] border-l border-slate-700 flex flex-col h-full" data-testid="properties-panel">
      {/* Header */}
      <div className="p-3 border-b border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" style={{ color: colors.bg }} />}
          <span className="text-sm font-bold text-white">{label}</span>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-3">
        <div className="space-y-4">
          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs text-slate-400">Title</Label>
            <Input value={node.data.title || ''} onChange={(e) => updateTitle(e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm" data-testid="prop-title" />
          </div>

          {/* TRIGGER */}
          {nt === 'trigger' && (
            <>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Trigger Type</Label>
                <select value={config.trigger_type || 'keyword'} onChange={(e) => updateConfig('trigger_type', e.target.value)} className="h-9 w-full rounded-md border border-slate-600 bg-slate-800 text-white px-3 text-sm" data-testid="prop-trigger-type">
                  <option value="keyword">Keyword</option>
                  <option value="any_message">Any Message</option>
                </select>
              </div>
              {config.trigger_type !== 'any_message' && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">Keyword</Label>
                  <Input value={config.keyword || ''} onChange={(e) => updateConfig('keyword', e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm" placeholder="hello" data-testid="prop-keyword" />
                </div>
              )}
            </>
          )}

          {/* TEXT / QUICK REPLY content */}
          {(nt === 'text_message' || nt === 'quick_reply') && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">{nt === 'quick_reply' ? 'Question/Prompt' : 'Message Text'}</Label>
              <Textarea value={config.content || ''} onChange={(e) => updateConfig('content', e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm" rows={4} data-testid="prop-content" />
            </div>
          )}

          {/* MEDIA nodes */}
          {['image_message', 'audio_message', 'video_message', 'document_message'].includes(nt) && (
            <>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Media URL</Label>
                <div className="flex gap-1">
                  <Input value={config.media_url || ''} onChange={(e) => updateConfig('media_url', e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm flex-1" placeholder="https://..." data-testid="prop-media-url" />
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-9" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  </Button>
                  <input ref={fileRef} type="file" className="hidden" accept="image/*,audio/*,video/*,.pdf,.doc,.docx" onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])} />
                </div>
              </div>
              {media && media.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">From Media Library</Label>
                  <select value={config.media_asset_id || ''} onChange={(e) => { const m = media.find(x => x.id === e.target.value); if (m) { updateConfig('media_url', m.file_url); updateConfig('media_asset_id', m.id); } }} className="h-9 w-full rounded-md border border-slate-600 bg-slate-800 text-white px-3 text-sm">
                    <option value="">Select asset...</option>
                    {media.map(m => <option key={m.id} value={m.id}>{m.name} ({m.file_type})</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Caption</Label>
                <Input value={config.content || ''} onChange={(e) => updateConfig('content', e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm" placeholder="Optional caption..." />
              </div>
            </>
          )}

          {/* QUICK REPLY options */}
          {nt === 'quick_reply' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-400">Options ({(config.options || []).length})</Label>
                <Button size="sm" variant="outline" className="h-6 text-xs border-slate-600 text-slate-300" onClick={addOption}>
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
              {(config.options || []).map((opt, idx) => (
                <div key={opt.id || idx} className="space-y-1 p-2 rounded bg-slate-800/50 border border-slate-700" style={{ borderLeftColor: '#06b6d4', borderLeftWidth: 3 }}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500 font-medium">Option {idx + 1}</span>
                    <div className="flex gap-0.5">
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-500 hover:text-white" onClick={() => moveOption(idx, -1)} disabled={idx === 0}><span className="text-[10px]">^</span></Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-500 hover:text-white" onClick={() => moveOption(idx, 1)} disabled={idx === (config.options || []).length - 1}><span className="text-[10px]">v</span></Button>
                      <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400" onClick={() => removeOption(idx)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                  <Input value={opt.label} onChange={(e) => updateOption(idx, 'label', e.target.value)} placeholder="Label (shown to user)" className="bg-slate-800 border-slate-600 text-white text-xs h-7" />
                  <Input value={opt.value} onChange={(e) => updateOption(idx, 'value', e.target.value)} placeholder="Trigger value" className="bg-slate-800 border-slate-600 text-white text-xs h-7" />
                </div>
              ))}
            </div>
          )}

          {/* DELAY */}
          {nt === 'delay' && (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Amount</Label>
                <Input type="number" value={config.delay_amount || 5} onChange={(e) => updateConfig('delay_amount', parseInt(e.target.value) || 0)} className="bg-slate-800 border-slate-600 text-white text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-400">Unit</Label>
                <select value={config.delay_unit || 'seconds'} onChange={(e) => updateConfig('delay_unit', e.target.value)} className="h-9 w-full rounded-md border border-slate-600 bg-slate-800 text-white px-3 text-sm">
                  <option value="seconds">Seconds</option>
                  <option value="minutes">Minutes</option>
                </select>
              </div>
            </div>
          )}

          {/* CONDITION */}
          {nt === 'condition' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-400">Branches ({(config.branches || []).length})</Label>
                <Button size="sm" variant="outline" className="h-6 text-xs border-slate-600 text-slate-300" onClick={addBranch}>
                  <Plus className="w-3 h-3 mr-1" />Add
                </Button>
              </div>
              {(config.branches || []).map((br, idx) => (
                <div key={br.id || idx} className="space-y-1 p-2 rounded bg-slate-800/50 border border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Branch {idx + 1}</span>
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-red-400" onClick={() => removeBranch(idx)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                  <select value={br.type} onChange={(e) => updateBranch(idx, 'type', e.target.value)} className="h-7 w-full rounded border border-slate-600 bg-slate-800 text-white px-2 text-xs">
                    <option value="equals">Equals</option>
                    <option value="contains">Contains</option>
                    <option value="fallback">Fallback</option>
                  </select>
                  <Input value={br.label} onChange={(e) => updateBranch(idx, 'label', e.target.value)} placeholder="Label" className="bg-slate-800 border-slate-600 text-white text-xs h-7" />
                  {br.type !== 'fallback' && (
                    <Input value={br.value || ''} onChange={(e) => updateBranch(idx, 'value', e.target.value)} placeholder="Match value" className="bg-slate-800 border-slate-600 text-white text-xs h-7" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* END */}
          {nt === 'end' && (
            <div className="space-y-1">
              <Label className="text-xs text-slate-400">End Message (optional)</Label>
              <Textarea value={config.message || ''} onChange={(e) => updateConfig('message', e.target.value)} className="bg-slate-800 border-slate-600 text-white text-sm" rows={3} placeholder="Optional goodbye message..." />
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
